import { Toaster } from '@/components/ui/Toaster';
import { Toaster as Sonner } from '@/components/ui/Sonner';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { TamaguiProvider } from 'tamagui';
import tamaguiConfig from '../tamagui.config';
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import { logVersionInfo } from './lib/version';
import ErrorBoundary from './components/ErrorBoundary';
import PageErrorBoundary from './components/PageErrorBoundary';
import ReportPage from './pages/ReportPage';
import ScanHistoryPage from './pages/ScanHistoryPage';
import AIBOMPage from './pages/AIBOMPage';
import ComplianceCrosswalkPage from './pages/ComplianceCrosswalkPage';
import PrintableReportPage from './pages/PrintableReportPage';
import BusinessSummaryPage from './pages/BusinessSummaryPage';
import LoginPage from './pages/LoginPage';
import MagicLinkPage from './pages/MagicLinkPage';
import DashboardPage from './pages/DashboardPage';
import AccountPage from './pages/AccountPage';
import PricingPage from './pages/PricingPage';
import WelcomePage from './pages/WelcomePage';
import NotFound from './pages/NotFound';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';
import DeviceVerifyPage from './pages/DeviceVerifyPage';
import DeviceCallbackPage from './pages/DeviceCallbackPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import CheckoutReturnPage from './pages/CheckoutReturnPage';
import SubscribePage from './pages/SubscribePage';
import DocsPage from './pages/DocsPage';
import McpDocsPage from './pages/McpDocsPage';
import ScanComparePage from './pages/ScanComparePage';
import AmbassadorPage from './pages/AmbassadorPage';
import Footer from './components/Footer';

// Log version info on app startup for debugging
logVersionInfo();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (cache time)
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnReconnect: false, // Don't refetch on reconnect
    },
    mutations: {
      retry: 0, // Don't retry mutations
    },
  },
});

const App = () => {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark">
      <TamaguiProvider config={tamaguiConfig} defaultTheme="dark">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <OrgProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <ErrorBoundary>
                  <div className="flex flex-col min-h-screen">
                  <Routes>
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="/report" element={<PageErrorBoundary pageName="Report"><ReportPage /></PageErrorBoundary>} />
                    <Route path="/signup" element={<Navigate to="/pricing" replace />} />
                    <Route path="/login" element={<PageErrorBoundary pageName="Login"><LoginPage /></PageErrorBoundary>} />
                    <Route path="/login/magic/:token" element={<PageErrorBoundary pageName="Magic Link"><MagicLinkPage /></PageErrorBoundary>} />
                    <Route path="/pricing" element={<PageErrorBoundary pageName="Pricing"><PricingPage /></PageErrorBoundary>} />
                    <Route path="/welcome" element={<PageErrorBoundary pageName="Welcome"><WelcomePage /></PageErrorBoundary>} />
                    <Route path="/dashboard" element={<PageErrorBoundary pageName="Dashboard"><DashboardPage /></PageErrorBoundary>} />
                    <Route path="/reports" element={<PageErrorBoundary pageName="Scan History"><ScanHistoryPage /></PageErrorBoundary>} />
                    <Route path="/scan-history" element={<Navigate to="/reports" replace />} />
                    <Route path="/account" element={<PageErrorBoundary pageName="Account"><AccountPage /></PageErrorBoundary>} />
                    <Route path="/ai-bom" element={<PageErrorBoundary pageName="AI BOM"><AIBOMPage /></PageErrorBoundary>} />
                    <Route path="/report/ai-bom" element={<PageErrorBoundary pageName="AI BOM"><AIBOMPage /></PageErrorBoundary>} />
                    <Route path="/compliance-crosswalk" element={<PageErrorBoundary pageName="Compliance Crosswalk"><ComplianceCrosswalkPage /></PageErrorBoundary>} />
                    <Route path="/report/compliance" element={<PageErrorBoundary pageName="Compliance Crosswalk"><ComplianceCrosswalkPage /></PageErrorBoundary>} />
                    <Route path="/report/print" element={<PageErrorBoundary pageName="Print Report"><PrintableReportPage /></PageErrorBoundary>} />
                    <Route path="/report/business-summary" element={<PageErrorBoundary pageName="Business Summary"><BusinessSummaryPage /></PageErrorBoundary>} />
                    <Route path="/oauth/authorize" element={<PageErrorBoundary pageName="OAuth"><OAuthAuthorizePage /></PageErrorBoundary>} />
                    <Route path="/device/verify" element={<PageErrorBoundary pageName="Device Verify"><DeviceVerifyPage /></PageErrorBoundary>} />
                    <Route path="/device/callback" element={<PageErrorBoundary pageName="Device Callback"><DeviceCallbackPage /></PageErrorBoundary>} />
                    <Route path="/auth/callback" element={<PageErrorBoundary pageName="Auth"><AuthCallbackPage /></PageErrorBoundary>} />
                    <Route path="/checkout/return" element={<PageErrorBoundary pageName="Checkout"><CheckoutReturnPage /></PageErrorBoundary>} />
                    <Route path="/subscribe/:tier" element={<PageErrorBoundary pageName="Subscribe"><SubscribePage /></PageErrorBoundary>} />
                    <Route path="/docs" element={<PageErrorBoundary pageName="Docs"><DocsPage /></PageErrorBoundary>} />
                    <Route path="/docs/mcp" element={<PageErrorBoundary pageName="MCP Docs"><McpDocsPage /></PageErrorBoundary>} />
                    <Route path="/compare" element={<PageErrorBoundary pageName="Scan Compare"><ScanComparePage /></PageErrorBoundary>} />
                    <Route path="/admin/ambassadors" element={<PageErrorBoundary pageName="Ambassadors"><AmbassadorPage /></PageErrorBoundary>} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <Footer />
                  </div>
                  </ErrorBoundary>
                </BrowserRouter>
              </TooltipProvider>
            </OrgProvider>
          </AuthProvider>
        </QueryClientProvider>
      </TamaguiProvider>
    </ThemeProvider>
  );
};

export default App;
